import React, { useState, useCallback } from 'react';
import { Box, Typography, Link, useMediaQuery } from '@mui/material';
import { DesktopWindowsOutlined as DesktopWindowsOutlinedIcon } from '@mui/icons-material';

/**
 * MobileNotice — full-screen overlay shown when the viewport is narrower than
 * 768px. Sets expectations rather than locking users out: "Continue anyway"
 * dismisses the overlay for the rest of the browser session.
 *
 * Why a gate (not full responsive rebuild)? Trading dashboards are
 * wide-table-heavy (Trades, Transactions, NAV, Reconciliation). Going phone-
 * friendly properly is multi-week work and out of scope for Beta v2 polish.
 * Audience is desktop-first systematic traders. This component just sets the
 * expectation while preserving an escape hatch.
 *
 * Behavior:
 *   - Renders null on viewports ≥ 768px (useMediaQuery handles the resize
 *     listener internally — no manual matchMedia bookkeeping).
 *   - Renders null if the user has already dismissed it this session
 *     (`sessionStorage['kodahfy_mobile_dismissed'] === '1'`).
 *   - Auto-hides when the user widens the viewport above 768px without any
 *     explicit interaction — useMediaQuery flips to false and the component
 *     short-circuits.
 *   - Dismissal persists per-session only, not per-user. Closing the tab and
 *     reopening on mobile shows the notice again, which is intentional —
 *     re-confirms the expectation and gives a fresh user new info.
 *
 * Mounted at App.jsx top level (inside ThemeProvider, before BrowserRouter)
 * so it appears on every route, including auth pages.
 */

const SESSION_KEY = 'kodahfy_mobile_dismissed';

const MobileNotice = () => {
  // 767px because the breakpoint is exclusive: 768px+ is desktop, 767 and below is mobile.
  // Custom query (not theme.breakpoints.down('md')=900px) per JC's exact spec.
  const isMobile = useMediaQuery('(max-width: 767px)');

  // Initialise from sessionStorage so a refresh on mobile after dismissal
  // still skips the overlay (within the same browser session).
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) === '1';
    } catch {
      // sessionStorage can throw in private browsing on some browsers — fall
      // through to "not dismissed" rather than crash on render.
      return false;
    }
  });

  const handleDismiss = useCallback((e) => {
    e.preventDefault();
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      // Same private-mode caveat as above. Component still dismisses for the
      // current page-load via the local state below; refresh would re-show.
    }
    setDismissed(true);
  }, []);

  if (!isMobile || dismissed) return null;

  return (
    <Box
      role="dialog"
      aria-modal="true"
      aria-labelledby="mobile-notice-title"
      sx={{
        position: 'fixed',
        inset: 0,                                      // top/right/bottom/left = 0
        zIndex: (theme) => theme.zIndex.modal + 100,   // above everything else
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 3,
        py: 6
      }}
    >
      <DesktopWindowsOutlinedIcon
        sx={{ fontSize: 64, color: 'var(--teal)', opacity: 0.85, mb: 2 }}
      />

      <Typography
        id="mobile-notice-title"
        variant="h5"
        sx={{ fontWeight: 600, mb: 1.5 }}
      >
        Kodahfy works best on desktop
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ maxWidth: 360, lineHeight: 1.5, mb: 3 }}
      >
        Trading dashboards work best on a bigger screen. We're focused on the
        desktop experience for now — full mobile support coming soon.
      </Typography>

      <Link
        component="button"
        onClick={handleDismiss}
        sx={{
          fontSize: '0.875rem',
          color: 'text.secondary',
          textDecoration: 'underline',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0
        }}
      >
        Continue anyway
      </Link>
    </Box>
  );
};

export default MobileNotice;
